import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Badge,
  Collapse,
} from '@mui/material';
import {
  People,
  Security,
  Refresh,
  AdminPanelSettings,
  Add,
  Delete,
  VpnKey,
  Business,
  PersonOutline,
  Assessment,
  Shield,
  Groups,
  CheckCircleOutline,
  BlockOutlined,
  Notifications,
  ExpandMore,
  ExpandLess,
  ThumbUp,
  ThumbDown,
  AccessTime,
} from '@mui/icons-material';
import {
  fetchUsers,
  updateUserRole,
  fetchAssetAccessRules,
  updateAssetAccessRule,
  fetchRoles,
  createRole,
  deleteRole,
  fetchDashboardStats,
  fetchCurrentUser,
  fetchAssessmentDomains,
  fetchResourceRequests,
  reviewResourceRequest,
  fetchAdminNotifications,
  markNotificationRead,
  fetchUserDomains,
  updateUserDomains,
  UserRecord,
  AssetAccessRule,
  RoleRecord,
  DashboardStats,
  AssessmentCategory,
  AssessmentSubDomain,
  ResourceRequestRecord,
  AdminNotificationRecord,
} from '../api/admin';

interface AdminDashboardProps {
  onBack?: () => void;
}

const ASSET_CLASSES = ['questions', 'sales_pitch', 'report_template', 'tool'];

const SYSTEM_ROLE_NAMES = [
  'super_admin', 'user_admin', 'engagement_manager', 'consultant', 'client',
  'sme_security_compliance', 'sme_ai_analytics',
  'sme_development_operations', 'sme_cloud_strategy_adoption',
  'sme_productivity_collaboration', 'sme_baseline_architecture',
];

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [accessRules, setAccessRules] = useState<AssetAccessRule[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Assessment domain categories (loaded from backend)
  const [categories, setCategories] = useState<AssessmentCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Resource requests
  const [resourceRequests, setResourceRequests] = useState<ResourceRequestRecord[]>([]);

  // Notifications
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [notifications, setNotifications] = useState<AdminNotificationRecord[]>([]);

  // Create role dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', display_name: '', description: '', is_domain_scoped: false });

  // Current user (for self-demotion detection)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  // Self-demotion confirmation dialog
  const [demotionDialog, setDemotionDialog] = useState<{ userId: string; newRole: string } | null>(null);

  // Super admin promotion warning dialog
  const [adminWarningDialog, setAdminWarningDialog] = useState<{ userId: string; currentCount: number } | null>(null);

  // Domain assignment dialog
  const [domainDialogUser, setDomainDialogUser] = useState<UserRecord | null>(null);
  const [domainDialogSelections, setDomainDialogSelections] = useState<Set<string>>(new Set());
  const [domainDialogLoading, setDomainDialogLoading] = useState(false);

  // ── Data loaders ──────────────────────────────────────────

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRoles();
      setRoles(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAccessRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAssetAccessRules();
      setAccessRules(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchDashboardStats();
      setStats(data);
    } catch {
      // Stats are supplementary — don't block the dashboard
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await fetchAssessmentDomains();
      setCategories(cats);
      // Start collapsed — user expands as needed
      setExpandedCategories(new Set());
    } catch {
      // Non-critical
    }
  }, []);

  const loadResourceRequests = useCallback(async () => {
    try {
      const data = await fetchResourceRequests();
      setResourceRequests(data);
    } catch {
      // Non-critical
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await fetchAdminNotifications();
      setPendingRequestCount(data.pending_requests);
      setNotifications(data.notifications);
    } catch {
      // Non-critical
    }
  }, []);

  // Load stats and current user on mount
  useEffect(() => {
    loadStats();
    loadCategories();
    loadNotifications();
    fetchCurrentUser()
      .then((u) => { setCurrentUserId(u.id); setCurrentUserRole(u.role); })
      .catch(() => {});
  }, [loadStats, loadCategories, loadNotifications]);

  useEffect(() => {
    if (tab === 0) { loadUsers(); fetchRoles().then(setRoles).catch(() => {}); }
    else if (tab === 1) loadRoles();
    else if (tab === 2) loadAccessRules();
    else if (tab === 3) loadResourceRequests();
  }, [tab, loadUsers, loadRoles, loadAccessRules, loadResourceRequests]);

  // ── Handlers ──────────────────────────────────────────────

  const handleRoleChange = async (userId: string, newRoleName: string) => {
    // Detect super_admin trying to change their own role
    if (
      userId === currentUserId &&
      currentUserRole === 'super_admin' &&
      newRoleName !== 'super_admin'
    ) {
      setDemotionDialog({ userId, newRole: newRoleName });
      return;
    }
    // Warn when promoting to super_admin and 5+ already exist
    if (newRoleName === 'super_admin') {
      const currentAdminCount = users.filter(u => u.role === 'super_admin').length;
      if (currentAdminCount >= 5) {
        setAdminWarningDialog({ userId, currentCount: currentAdminCount });
        return;
      }
    }
    await executeRoleChange(userId, newRoleName);
  };

  const executeRoleChange = async (userId: string, newRoleName: string) => {
    setError(null);
    setSuccess(null);
    try {
      await updateUserRole(userId, newRoleName);
      setUsers((prev) =>
        prev.map((u) => {
          if (u.id !== userId) return u;
          const roleObj = roles.find((r) => r.name === newRoleName);
          return { ...u, role: newRoleName, role_display_name: roleObj?.display_name || newRoleName };
        })
      );
      // Update local state if the current user changed their own role
      if (userId === currentUserId) {
        setCurrentUserRole(newRoleName);
      }
      setSuccess('Role updated successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateRole = async () => {
    setError(null);
    setSuccess(null);
    try {
      await createRole(newRole);
      setCreateDialogOpen(false);
      setNewRole({ name: '', display_name: '', description: '', is_domain_scoped: false });
      await loadRoles();
      setSuccess('Role created successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteRole = async (roleName: string) => {
    if (!window.confirm(`Delete role "${roleName}"? Users with this role will be moved to "client".`)) return;
    setError(null);
    setSuccess(null);
    try {
      await deleteRole(roleName);
      await loadRoles();
      setSuccess('Role deleted');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleAccess = async (
    assessmentType: string,
    assetClass: string,
    currentValue: boolean
  ) => {
    // Optimistic update — toggle immediately in local state
    const newValue = !currentValue;
    setAccessRules((prev) => {
      const existing = prev.find(
        (r) => r.assessment_type === assessmentType && r.asset_class === assetClass
      );
      if (existing) {
        return prev.map((r) => (r === existing ? { ...r, client_visible: newValue } : r));
      }
      return [...prev, { assessment_type: assessmentType, asset_class: assetClass, client_visible: newValue }];
    });

    try {
      await updateAssetAccessRule({
        assessment_type: assessmentType,
        asset_class: assetClass,
        client_visible: newValue,
      });
    } catch (err: any) {
      // Revert on failure
      setAccessRules((prev) => {
        const existing = prev.find(
          (r) => r.assessment_type === assessmentType && r.asset_class === assetClass
        );
        if (existing) {
          return prev.map((r) => (r === existing ? { ...r, client_visible: currentValue } : r));
        }
        return prev;
      });
      setError(err.message);
    }
  };

  const isClientVisible = (at: string, ac: string): boolean =>
    accessRules.find((r) => r.assessment_type === at && r.asset_class === ac)?.client_visible ?? false;

  const handleReviewRequest = async (requestId: string, action: 'approve' | 'reject') => {
    setError(null);
    setSuccess(null);
    try {
      await reviewResourceRequest(requestId, action);
      setResourceRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: action === 'approve' ? 'approved' : 'rejected' } : r))
      );
      setSuccess(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      loadNotifications();
      if (action === 'approve') loadAccessRules();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleMarkNotificationRead = async (id: number) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      // Non-critical
    }
  };

  // ── Domain assignment handlers ────────────────────────────

  const handleOpenDomainDialog = async (user: UserRecord) => {
    setDomainDialogUser(user);
    setDomainDialogLoading(true);
    try {
      const assigned = await fetchUserDomains(user.id);
      setDomainDialogSelections(new Set(assigned));
    } catch {
      setDomainDialogSelections(new Set());
    }
    setDomainDialogLoading(false);
  };

  const handleToggleDomainSelection = (domainId: string) => {
    setDomainDialogSelections((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId);
      else next.add(domainId);
      return next;
    });
  };

  const handleToggleCategoryDomains = (cat: AssessmentCategory) => {
    const catDomainIds = cat.sub_domains.map((sd) => sd.domain_id);
    const allSelected = catDomainIds.every((d) => domainDialogSelections.has(d));
    setDomainDialogSelections((prev) => {
      const next = new Set(prev);
      catDomainIds.forEach((d) => (allSelected ? next.delete(d) : next.add(d)));
      return next;
    });
  };

  const handleSaveDomainAssignments = async () => {
    if (!domainDialogUser) return;
    setError(null);
    try {
      await updateUserDomains(domainDialogUser.id, Array.from(domainDialogSelections));
      setSuccess(`Domain assignments updated for ${domainDialogUser.name || domainDialogUser.email}`);
      setDomainDialogUser(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const toggleCategory = (catName: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catName)) next.delete(catName);
      else next.add(catName);
      return next;
    });
    // After the Collapse animation, scroll the category header into view
    setTimeout(() => {
      const el = document.getElementById(`cat-header-${catName.replace(/\s+/g, '-')}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
  };

  // Compute all unique assessment type IDs from categories for summary counts
  const allDomainIds = Array.from(new Set(categories.flatMap((c) => c.sub_domains.map((sd) => sd.domain_id))));


  const roleColor = (role: string): 'error' | 'warning' | 'primary' | 'info' | 'success' | 'default' => {
    if (role === 'super_admin') return 'error';
    if (role === 'user_admin') return 'warning';
    if (role === 'engagement_manager') return 'warning';
    if (role.startsWith('sme_')) return 'info';
    if (role === 'consultant') return 'primary';
    if (role === 'client') return 'success';
    return 'default';
  };

  const refreshHandler = () => {
    loadStats();
    loadNotifications();
    if (tab === 0) loadUsers();
    else if (tab === 1) loadRoles();
    else if (tab === 2) { loadAccessRules(); loadCategories(); }
    else if (tab === 3) loadResourceRequests();
  };

  // Split users into KPMG staff and external clients
  const kpmgStaff = users.filter((u) => u.is_kpmg);
  const externalClients = users.filter((u) => !u.is_kpmg);

  // Helper to render a user table for a given subset
  const renderUserTable = (userList: UserRecord[], emptyMessage: string) => (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Organization</strong></TableCell>
            <TableCell><strong>Current Role</strong></TableCell>
            <TableCell><strong>Assign Role</strong></TableCell>
            <TableCell><strong>Domains</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {userList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            userList.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.organization_name}</TableCell>
                <TableCell>
                  <Chip
                    label={u.role_display_name || u.role}
                    color={roleColor(u.role) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      size="small"
                    >
                      {roles.map((r) => (
                        <MenuItem key={r.name} value={r.name}>
                          {r.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Tooltip title="Assign assessment domains">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDomainDialog(u)}
                      sx={{ color: '#00338D' }}
                    >
                      <VpnKey fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminPanelSettings sx={{ mr: 1, color: '#00338D' }} />
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Admin Dashboard
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refreshHandler}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* ── Summary Stats Cards ────────────────────────── */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Users', value: stats.users.total, icon: <People />, color: '#00338D' },
            { label: 'KPMG Staff', value: stats.users.kpmg_staff, icon: <Business />, color: '#00338D' },
            { label: 'Client Users', value: stats.users.clients, icon: <PersonOutline />, color: '#483698' },
            { label: 'Roles Defined', value: stats.roles.total, icon: <VpnKey />, color: '#470A68' },
            { label: 'Assessments Run', value: stats.assessments.total, icon: <Assessment />, color: '#00A3A1' },
          ].map((card) => (
            <Paper
              key={card.label}
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: card.color }}>
                {card.icon}
                <Typography variant="caption" sx={{ ml: 1, fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {card.label}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>
                {card.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Role Assignment Breakdown & Assessments by Domain ── */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          {/* Role assignment breakdown */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Groups sx={{ mr: 1, color: '#00338D' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Role Assignments</Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {stats.roles.breakdown
                  .filter((r) => r.user_count > 0)
                  .sort((a, b) => b.user_count - a.user_count)
                  .map((r) => (
                    <TableRow key={r.name}>
                      <TableCell sx={{ border: 0, py: 0.75 }}>
                        <Chip label={r.display_name} color={roleColor(r.name) as any} size="small" />
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.75, fontWeight: 600 }}>
                        {r.user_count}
                      </TableCell>
                    </TableRow>
                  ))}
                {stats.roles.breakdown.filter((r) => r.user_count > 0).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ border: 0, color: 'text.secondary' }}>No role assignments yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Assessments by domain */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assessment sx={{ mr: 1, color: '#00A3A1' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Assessments by Domain</Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {stats.assessments.by_category.length > 0 ? (
                  stats.assessments.by_category
                    .sort((a, b) => b.count - a.count)
                    .map((d) => (
                      <TableRow key={d.category}>
                        <TableCell sx={{ border: 0, py: 0.75 }}>
                          <Typography variant="body2">{d.category}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 0.75 }}>
                          <Chip label={d.count} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ border: 0, color: 'text.secondary' }}>No assessments run yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab icon={<People />} label="User Management" />
          <Tab icon={<VpnKey />} label="Role Management" />
          <Tab icon={<Security />} label="Asset Access Control" />
          <Tab
            icon={
              <Badge badgeContent={pendingRequestCount} color="error" max={99}>
                <Notifications />
              </Badge>
            }
            label="Resource Requests"
          />
        </Tabs>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ── Tab 0: User Management ──────────────────────── */}
      {tab === 0 && !loading && (
        <Box>
          {/* KPMG Staff / Professionals / Consultants */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Business sx={{ mr: 1, color: '#00338D' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#00338D' }}>
              KPMG Staff &amp; Consultants
            </Typography>
            <Chip label={kpmgStaff.length} size="small" sx={{ ml: 1, backgroundColor: '#00338D', color: '#fff' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            KPMG professionals and consultants. Roles can be elevated or changed by a Super Admin.
          </Typography>
          {renderUserTable(kpmgStaff, 'No KPMG staff have logged in yet')}

          <Divider sx={{ my: 3 }} />

          {/* External Clients */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonOutline sx={{ mr: 1, color: '#483698' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#483698' }}>
              External Clients
            </Typography>
            <Chip label={externalClients.length} size="small" sx={{ ml: 1, backgroundColor: '#483698', color: '#fff' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            External client users who have logged into the platform. Role changes are restricted.
          </Typography>
          {renderUserTable(externalClients, 'No external clients have logged in yet')}
        </Box>
      )}

      {/* ── Tab 1: Role Management ──────────────────────── */}
      {tab === 1 && !loading && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ backgroundColor: '#00338D' }}
            >
              Create Role
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell align="center"><strong>Users</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((r) => {
                  const isSystem = SYSTEM_ROLE_NAMES.includes(r.name);
                  return (
                    <TableRow key={r.name}>
                      <TableCell>
                        <Chip label={r.display_name} color={roleColor(r.name) as any} size="small" />
                      </TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.is_domain_scoped ? 'Domain SME' : 'Platform'}
                          variant="outlined"
                          size="small"
                        />
                        {isSystem && (
                          <Chip label="System" variant="outlined" size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">{r.user_count}</TableCell>
                      <TableCell align="center">
                        {!isSystem && (
                          <Tooltip title="Delete role">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRole(r.name)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Create Role Dialog */}
          <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Roles control what users can see and do on the platform. <strong>Platform roles</strong> (e.g. Consultant, Client) grant
                general access levels. <strong>Domain-scoped SME roles</strong> grant specialist access to a specific assessment domain
                (e.g. &quot;SME &ndash; Security &amp; Compliance&quot;), allowing the user to review and validate assessments in that domain.
              </Alert>
              <TextField
                label="Role Display Name"
                placeholder="e.g. SME – Data Governance"
                value={newRole.display_name}
                onChange={(e) => {
                  const dn = e.target.value;
                  setNewRole({
                    ...newRole,
                    display_name: dn,
                    name: dn.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                  });
                }}
                fullWidth
                required
                helperText="The name shown to users in the dashboard and role picker"
              />
              <TextField
                label="Internal Identifier"
                placeholder="e.g. sme_data_governance"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                fullWidth
                helperText="Auto-generated from display name. Lowercase with underscores, used internally by the system."
              />
              <TextField
                label="Description"
                placeholder="e.g. Subject Matter Expert for Data Governance assessments"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                helperText="Explain what this role grants access to"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newRole.is_domain_scoped}
                    onChange={(e) => setNewRole({ ...newRole, is_domain_scoped: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Domain-scoped SME role</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Check this for Subject Matter Expert roles tied to a specific assessment domain.
                      SME users get full access to assets and validation within their domain.
                    </Typography>
                  </Box>
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCreateRole}
                disabled={!newRole.name || !newRole.display_name}
                sx={{ backgroundColor: '#00338D' }}
              >
                Create
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* ── Tab 2: Asset Access Control ─────────────────── */}
      {tab === 2 && !loading && (
        <Box>
          {/* Summary bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Shield sx={{ color: '#00338D', fontSize: 20 }} />
              <Typography variant="body2"><strong>{categories.reduce((s, c) => s + c.sub_domains.length, 0)}</strong> sub-domains across <strong>{categories.length}</strong> categories</Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutline sx={{ color: '#2E7D32', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>{allDomainIds.reduce((sum, t) => sum + ASSET_CLASSES.filter((c) => isClientVisible(t, c)).length, 0)}</strong> of {allDomainIds.length * ASSET_CLASSES.length} asset types enabled for clients
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlockOutlined sx={{ color: '#D32F2F', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>{allDomainIds.reduce((sum, t) => sum + ASSET_CLASSES.filter((c) => !isClientVisible(t, c)).length, 0)}</strong> hidden from clients
              </Typography>
            </Paper>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Control which asset types are visible to <strong>Client</strong> users for each assessment sub-domain.
            <strong> Super Admin</strong>, <strong>User Admin</strong>, <strong>Consultant</strong>, and <strong>SME</strong> users always have full access.
            Sub-domains marked with a <Chip label="shared" size="small" variant="outlined" sx={{ mx: 0.5, height: 18 }} /> badge share the same underlying rules.
          </Alert>

          {categories.length === 0 ? (
            <Alert severity="warning">No assessment domains configured. Check backend connectivity.</Alert>
          ) : (
            categories.map((cat) => {
              const catEnabledCount = cat.sub_domains.reduce(
                (sum, sd) => sum + ASSET_CLASSES.filter((c) => isClientVisible(sd.domain_id, c)).length, 0
              );
              const catTotalSlots = cat.sub_domains.length * ASSET_CLASSES.length;

              return (
                <Paper key={cat.name} elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  {/* Category header — collapsible */}
                  <Box
                    id={`cat-header-${cat.name.replace(/\s+/g, '-')}`}
                    sx={{
                      display: 'flex', alignItems: 'center', px: 2, py: 1.5,
                      backgroundColor: '#f5f5f5', cursor: 'pointer',
                      '&:hover': { backgroundColor: '#eeeeee' },
                    }}
                    onClick={() => toggleCategory(cat.name)}
                  >
                    {expandedCategories.has(cat.name) ? <ExpandLess sx={{ mr: 1 }} /> : <ExpandMore sx={{ mr: 1 }} />}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{cat.name}</Typography>
                    <Chip
                      label={`${cat.sub_domains.length} sub-domains`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={`${catEnabledCount}/${catTotalSlots} enabled`}
                      size="small"
                      color={catEnabledCount === catTotalSlots ? 'success' : catEnabledCount > 0 ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </Box>

                  <Collapse in={expandedCategories.has(cat.name)}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'grey.50' }}>
                            <TableCell sx={{ minWidth: 280 }}><strong>Assessment Sub-Domain</strong></TableCell>
                            {ASSET_CLASSES.map((cls) => (
                              <TableCell key={cls} align="center" sx={{ minWidth: 100 }}>
                                <strong>{cls.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</strong>
                              </TableCell>
                            ))}
                            <TableCell align="center"><strong>Client Access</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cat.sub_domains.map((sd: AssessmentSubDomain) => {
                            const enabledCount = ASSET_CLASSES.filter((c) => isClientVisible(sd.domain_id, c)).length;
                            return (
                              <TableRow key={sd.domain_id}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {sd.name}
                                  </Typography>
                                </TableCell>
                                {ASSET_CLASSES.map((cls) => (
                                  <TableCell key={cls} align="center">
                                    <Switch
                                      checked={isClientVisible(sd.domain_id, cls)}
                                      onChange={() => handleToggleAccess(sd.domain_id, cls, isClientVisible(sd.domain_id, cls))}
                                      color="success"
                                      size="small"
                                    />
                                  </TableCell>
                                ))}
                                <TableCell align="center">
                                  <Chip
                                    label={`${enabledCount}/${ASSET_CLASSES.length}`}
                                    size="small"
                                    color={enabledCount === ASSET_CLASSES.length ? 'success' : enabledCount > 0 ? 'warning' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Paper>
              );
            })
          )}
        </Box>
      )}

      {/* ── Tab 3: Resource Requests ────────────────────── */}
      {tab === 3 && !loading && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Client users can request access to restricted asset classes. Approving a request automatically
            enables the corresponding asset access rule. Notifications are sent to the requester.
          </Alert>

          {resourceRequests.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography color="text.secondary">No resource requests yet.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell><strong>Requester</strong></TableCell>
                    <TableCell><strong>Assessment Domain</strong></TableCell>
                    <TableCell><strong>Asset Class</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Submitted</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resourceRequests.map((rr) => (
                    <TableRow key={rr.id} sx={rr.status === 'pending' ? { backgroundColor: '#fff8e1' } : undefined}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{rr.user_name || 'Unknown'}</Typography>
                        <Typography variant="caption" color="text.secondary">{rr.user_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{rr.assessment_type.replace(/_/g, ' ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rr.asset_class.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rr.reason || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            rr.status === 'pending' ? <AccessTime fontSize="small" /> :
                            rr.status === 'approved' ? <ThumbUp fontSize="small" /> :
                            <ThumbDown fontSize="small" />
                          }
                          label={rr.status}
                          size="small"
                          color={rr.status === 'pending' ? 'warning' : rr.status === 'approved' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {rr.created_at ? new Date(rr.created_at).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {rr.status === 'pending' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Approve — grants access">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleReviewRequest(rr.id, 'approve')}
                              >
                                <ThumbUp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReviewRequest(rr.id, 'reject')}
                              >
                                <ThumbDown fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {rr.reviewed_at ? new Date(rr.reviewed_at).toLocaleDateString() : '—'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Notification history */}
          {notifications.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent Notifications</Typography>
              {notifications.slice(0, 10).map((n) => (
                <Paper
                  key={n.id}
                  elevation={0}
                  sx={{
                    p: 1.5, mb: 1, border: '1px solid', borderRadius: 1,
                    borderColor: n.is_read ? 'divider' : 'primary.main',
                    backgroundColor: n.is_read ? 'transparent' : '#e3f2fd',
                    cursor: n.is_read ? 'default' : 'pointer',
                  }}
                  onClick={() => !n.is_read && handleMarkNotificationRead(n.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Notifications fontSize="small" color={n.is_read ? 'disabled' : 'primary'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 600 }}>{n.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{n.message}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Domain Assignment Dialog ────────────────── */}
      <Dialog
        open={!!domainDialogUser}
        onClose={() => setDomainDialogUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#00338D' }}>
          Assign Domains — {domainDialogUser?.name || domainDialogUser?.email}
        </DialogTitle>
        <DialogContent dividers>
          {domainDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the assessment domains this user can access. Only assigned domains will be visible in assessments and assets.
              </Typography>
              {categories.map((cat) => {
                const catDomainIds = cat.sub_domains.map((sd) => sd.domain_id);
                const allChecked = catDomainIds.length > 0 && catDomainIds.every((d) => domainDialogSelections.has(d));
                const someChecked = catDomainIds.some((d) => domainDialogSelections.has(d));
                return (
                  <Box key={cat.name} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={() => handleToggleCategoryDomains(cat)}
                          sx={{ color: '#00338D', '&.Mui-checked': { color: '#00338D' } }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {cat.name}
                          <Chip
                            label={`${catDomainIds.filter((d) => domainDialogSelections.has(d)).length}/${catDomainIds.length}`}
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        </Typography>
                      }
                    />
                    <Box sx={{ pl: 4 }}>
                      {cat.sub_domains.map((sd) => (
                        <FormControlLabel
                          key={sd.domain_id}
                          control={
                            <Checkbox
                              checked={domainDialogSelections.has(sd.domain_id)}
                              onChange={() => handleToggleDomainSelection(sd.domain_id)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{sd.name}</Typography>}
                          sx={{ display: 'block', mb: -0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainDialogUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDomainAssignments}
            disabled={domainDialogLoading}
            sx={{ backgroundColor: '#00338D' }}
          >
            Save Assignments
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Super Admin promotion warning dialog ─────────── */}
      <Dialog
        open={!!adminWarningDialog}
        onClose={() => setAdminWarningDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          ⚠️ Super Admin Role Limit Recommendation
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            There are currently <strong>{adminWarningDialog?.currentCount ?? 0}</strong> users with the Super Admin role in this tenant.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            It is recommended to limit the number of Super Admin users to <strong>5 per tenant</strong> for security best practices:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li><Typography variant="body2">Super Admins have <strong>full unrestricted access</strong> to all system settings.</Typography></li>
            <li><Typography variant="body2">Excessive admin accounts increase the <strong>attack surface</strong> and risk of misuse.</Typography></li>
            <li><Typography variant="body2">Prefer assigning role-specific permissions (EM, SME, Consultant) where possible.</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary">
            You may still proceed — this is a recommendation, not a hard limit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminWarningDialog(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (adminWarningDialog) {
                await executeRoleChange(adminWarningDialog.userId, 'super_admin');
              }
              setAdminWarningDialog(null);
            }}
            variant="contained"
            color="warning"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Self-demotion confirmation dialog ─────────── */}
      <Dialog
        open={!!demotionDialog}
        onClose={() => setDemotionDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#D32F2F', fontWeight: 600 }}>
          ⚠️ Warning: Removing Your Super Admin Role
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to remove your own <strong>Super Admin</strong> role.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            If you proceed:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li><Typography variant="body2">You will <strong>lose admin dashboard access</strong> immediately.</Typography></li>
            <li><Typography variant="body2">You will <strong>not be able to reassign</strong> Super Admin to yourself.</Typography></li>
            <li><Typography variant="body2">Only another Super Admin can restore your access.</Typography></li>
          </Box>
          <Typography variant="body2" color="text.secondary">
            New role: <strong>{demotionDialog ? roles.find(r => r.name === demotionDialog.newRole)?.display_name || demotionDialog.newRole : ''}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemotionDialog(null)} variant="contained" sx={{ backgroundColor: '#00338D' }}>
            Cancel — Keep Super Admin
          </Button>
          <Button
            onClick={async () => {
              if (demotionDialog) {
                await executeRoleChange(demotionDialog.userId, demotionDialog.newRole);
              }
              setDemotionDialog(null);
            }}
            color="error"
            variant="outlined"
          >
            Proceed — Change My Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
